import {AREAS_DE_ACCESO, type Tema} from '@cnc/contracts';
import {useEffect, useState} from 'react';
import {NavLink, Outlet, useLocation, useNavigate} from 'react-router';
import {useAgenteDeCalidad} from '../api/calidad';
import {useResumenDeCumplimiento} from '../api/cumplimiento';
import {useGuardarPreferencias, useSalir} from '../api/sesion';
import {tr, trx, txt} from '../i18n/idioma';
import {AsistenteDeAyuda} from './asistente-de-ayuda';
import {AvisoDeLogros} from './aviso-de-logros';
import {DescansaPantalla} from './descansa-pantalla';
import {SelectorDeIdioma} from './selector-de-idioma';
import {nivelDe, useUsuario} from './sesion-contexto';
import {primeraRutaDe, ve} from './areas';
import {aplicarTema, iconoDeTema, nombreDeTema, useTema} from './usar-tema';

// Menu por secciones (aprobado por Daniel el 6/10/2026). Cada enlace dice que
// permiso necesita; una seccion sin enlaces visibles no se muestra. Los logros
// estan en Mi cuenta.
// seccion: la seccion o pestana que debe ver la persona (decision 0062); el
// enlace de una seccion con pestanas va a la primera que vea.
type Enlace = {
  a: string;
  texto: ReturnType<typeof txt>;
  icono: string;
  permiso?: 'editar' | 'administrar';
  seccion?: string;
};
const secciones: Array<{titulo?: ReturnType<typeof txt>; enlaces: Enlace[]}> = [
  {
    enlaces: [
      {a: '/', texto: txt('Inicio', 'Home'), icono: '⌂'},
      {a: '/inventario', texto: txt('Inventario', 'Inventory'), icono: '▤', seccion: 'inventario'},
      {a: '/seguimiento', texto: txt('Exportaciones', 'Exports'), icono: '⚓', seccion: 'exportaciones'},
      // Planeador semanal de la bodega de Cartagena (decision 0063).
      {a: '/planeador', texto: txt('Planeador semanal', 'Weekly planner'), icono: '📅', seccion: 'planeador'},
      // Dore (decision 0057): seguimiento de las exportaciones de dore.
      {a: '/dore', texto: txt('Doré', 'Doré'), icono: '◆', seccion: 'dore'},
      // Cashflow (decision 0042): flujos de dinero, financiaciones y cupos.
      {a: '/cashflow', texto: txt('Cashflow', 'Cashflow'), icono: '$', seccion: 'cashflow'},
      // Cumplimiento (decision 0045): documentos de proveedores, clientes y propios.
      {a: '/cumplimiento', texto: txt('Cumplimiento', 'Compliance'), icono: '⚖', seccion: 'cumplimiento'},
      {a: '/cargar', texto: txt('Cargar archivos', 'Upload files'), icono: '⇪', permiso: 'editar'},
    ],
  },
  {
    titulo: txt('Análisis', 'Analysis'),
    enlaces: [
      {a: '/analisis/reportes', texto: txt('Reportes', 'Pivot reports'), icono: '▥', seccion: 'analisis.reportes'},
      {a: '/analisis/informes', texto: txt('Informes', 'Excel reports'), icono: '⇩', seccion: 'analisis.informes'},
      {a: '/calidad', texto: txt('Calidad de datos', 'Data quality'), icono: '◇', seccion: 'analisis.calidad'},
    ],
  },
  {
    titulo: txt('Administración', 'Administration'),
    enlaces: [
      {a: '/administracion/usuarios', texto: txt('Usuarios', 'Users'), icono: '👤', permiso: 'administrar'},
      {a: '/administracion/bitacora', texto: txt('Bitácora', 'Audit log'), icono: '≡', permiso: 'administrar'},
      {a: '/administracion/configuracion', texto: txt('Configuración', 'Settings'), icono: '⚙', permiso: 'administrar'},
      {a: '/administracion/documentos', texto: txt('Documentos', 'Documents'), icono: '▣', permiso: 'administrar'},
      {
        a: '/administracion/carpetas',
        texto: txt('Carpetas en Dropbox', 'Dropbox folders'),
        icono: '🗀',
        permiso: 'administrar',
      },
      {
        a: '/configuracion-de-campos',
        texto: txt('Campos y catálogos', 'Fields and catalogs'),
        icono: '☷',
        permiso: 'editar',
        seccion: 'catalogos',
      },
      {a: '/estado', texto: txt('Estado del sistema', 'System status'), icono: '◎'},
      {a: '/guia', texto: txt('Guía de uso', 'User guide'), icono: '📖'},
    ],
  },
];

// Pantalla de computador o tableta (768 px o mas); por debajo, celular.
const CONSULTA_ANCHA = '(min-width: 768px)';
function useEsPantallaAncha() {
  const [ancha, setAncha] = useState(() =>
    typeof window === 'undefined' || !window.matchMedia ? true : window.matchMedia(CONSULTA_ANCHA).matches,
  );
  useEffect(() => {
    if (!window.matchMedia) return;
    const consulta = window.matchMedia(CONSULTA_ANCHA);
    const cambiar = () => setAncha(consulta.matches);
    consulta.addEventListener('change', cambiar);
    return () => consulta.removeEventListener('change', cambiar);
  }, []);
  return ancha;
}

const iniciales = (nombre: string) =>
  nombre
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0]!.toUpperCase())
    .join('');

// Estructura general: barra lateral con la marca, la navegacion (segun el
// rol), la persona en sesion, el idioma y el tema; el area de trabajo a la
// derecha. La barra se contrae a solo los simbolos y eso queda en el perfil
// (decision 0023), igual que el tema claro u oscuro.
export function Marco() {
  const usuario = useUsuario();
  const salir = useSalir();
  const navegar = useNavigate();
  const guardar = useGuardarPreferencias();
  const veCalidad = ve(usuario, 'analisis.calidad');
  const veCumplimiento = ve(usuario, 'cumplimiento');
  const agente = useAgenteDeCalidad(veCalidad);
  const alertasNuevas = agente.data?.nuevas ?? 0;
  const cumplimiento = useResumenDeCumplimiento(veCumplimiento);
  const documentosPorRenovar = cumplimiento.data?.avisos ?? 0;

  const [contraidoEnPerfil, setContraido] = useState(usuario.preferencias.menuContraido === true);
  // En celular (pantalla angosta) el menu va escondido tras "☰ Menú" y se abre
  // como panel encima (recorrido 7/10/2026: ocupaba dos tercios de la pantalla).
  const esAncho = useEsPantallaAncha();
  const contraido = contraidoEnPerfil && esAncho;
  const [abiertoEnCelular, setAbiertoEnCelular] = useState(false);
  const {pathname} = useLocation();
  useEffect(() => setAbiertoEnCelular(false), [pathname]);
  const [tema, setTema] = useState<Tema>(usuario.preferencias.tema ?? 'claro');
  useEffect(() => setTema(usuario.preferencias.tema ?? 'claro'), [usuario.preferencias.tema]);
  useTema(tema);

  const alternarMenu = () => {
    const nuevo = !contraidoEnPerfil;
    setContraido(nuevo);
    void guardar.mutateAsync({columnas: {}, menuContraido: nuevo}).catch(() => undefined);
  };
  // En el menu contraido el boton alterna entre claro y oscuro.
  const alternarTema = () => {
    const nuevo: Tema = document.documentElement.dataset.tema === 'oscuro' ? 'claro' : 'oscuro';
    aplicarTema(nuevo);
    setTema(nuevo);
    void guardar.mutateAsync({columnas: {}, tema: nuevo}).catch(() => undefined);
  };

  const clase = ({isActive}: {isActive: boolean}) =>
    // Tema "Ejecutiva clara": lo activo va en blanco con un subrayado de oro.
    `relative flex items-center gap-3 rounded-lg py-2 text-sm transition ${contraido ? 'justify-center px-2' : 'px-3'} ${
      isActive
        ? 'bg-white font-semibold text-verde-oscuro shadow-[inset_0_-2px_0_var(--color-dorado)]'
        : 'text-tinta hover:bg-white/70'
    }`;
  const cerrarSesion = async () => {
    await salir.mutateAsync().catch(() => undefined);
    void navegar('/ingresar', {replace: true});
  };
  const enlace = ({a, texto, icono}: {a: string; texto: ReturnType<typeof txt>; icono: string}) => {
    const nombre = trx(texto);
    const deCumplimiento = a === '/cumplimiento';
    const aviso = a === '/calidad' ? alertasNuevas : deCumplimiento ? documentosPorRenovar : 0;
    const queAvisa = deCumplimiento
      ? tr(`${aviso} documentos vencidos o por vencer`, `${aviso} documents expired or expiring soon`)
      : tr(`${aviso} alertas nuevas`, `${aviso} new alerts`);
    const titulo = aviso ? `${nombre}: ${queAvisa}` : contraido ? nombre : undefined;
    return (
      <NavLink key={a} to={a} end={a === '/'} className={clase} title={titulo}>
        <span aria-hidden className="w-5 text-center text-apagado">
          {icono}
        </span>
        <span className={contraido ? 'sr-only' : ''}>{nombre}</span>
        {aviso > 0 && (
          <span
            className={`rounded-full bg-alerta text-[10px] leading-4 font-semibold text-white ${
              contraido ? 'absolute top-0.5 right-0.5 min-w-4 px-1 text-center' : 'ml-auto px-1.5'
            }`}
            data-prueba={deCumplimiento ? 'aviso-cumplimiento' : 'aviso-calidad'}
          >
            <span aria-hidden>{aviso}</span>
            <span className="sr-only">{queAvisa}</span>
          </span>
        )}
      </NavLink>
    );
  };

  return (
    <div className="flex min-h-full">
      {!esAncho && abiertoEnCelular && (
        <button
          type="button"
          aria-label={tr('Cerrar el menú', 'Close the menu')}
          onClick={() => setAbiertoEnCelular(false)}
          className="fixed inset-0 z-30 bg-black/40"
        />
      )}
      <aside
        className={
          esAncho
            ? `flex shrink-0 flex-col border-r border-linea bg-papel transition-[width] ${contraido ? 'w-16' : 'w-64'}`
            : `fixed inset-y-0 left-0 z-40 flex w-64 flex-col overflow-y-auto border-r border-linea bg-papel transition-transform ${
                abiertoEnCelular ? 'translate-x-0' : '-translate-x-full'
              }`
        }
      >
        <div
          className={`flex items-start border-b border-linea py-5 ${contraido ? 'justify-center px-2' : 'justify-between px-5'}`}
        >
          {!contraido && (
            <div>
              <div className="marca text-lg font-semibold text-verde-oscuro">SUN VALLEY</div>
              <div className="mt-0.5 text-[11px] tracking-wide text-apagado uppercase">
                {tr('CNC · Inventario y exportaciones', 'CNC · Inventory and exports')}
              </div>
            </div>
          )}
          {contraido && <span className="sr-only">Sun Valley</span>}
          <button
            type="button"
            onClick={esAncho ? alternarMenu : () => setAbiertoEnCelular(false)}
            aria-expanded={!contraido}
            aria-label={
              !esAncho
                ? tr('Cerrar el menú', 'Close the menu')
                : contraido
                  ? tr('Expandir el menú', 'Expand the menu')
                  : tr('Contraer el menú', 'Collapse the menu')
            }
            title={contraido ? tr('Expandir el menú', 'Expand the menu') : tr('Contraer el menú', 'Collapse the menu')}
            className="rounded-md px-1.5 py-0.5 text-apagado hover:bg-papel hover:text-tinta"
          >
            {contraido ? '»' : '«'}
          </button>
        </div>
        <nav
          className={`flex flex-1 flex-col gap-1 ${contraido ? 'p-2' : 'p-3'}`}
          aria-label={tr('Secciones', 'Sections')}
        >
          {secciones.map((seccion, i) => {
            const visibles = seccion.enlaces
              .filter(e => !e.permiso || usuario.permisos[e.permiso])
              .filter(e => e.a !== '/cargar' || AREAS_DE_ACCESO.some(a => nivelDe(usuario, a) === 'editar'))
              .flatMap(e => {
                if (!e.seccion) return [e];
                const a = e.seccion.includes('.')
                  ? ve(usuario, e.seccion)
                    ? e.a
                    : null
                  : primeraRutaDe(usuario, e.seccion);
                return a ? [{...e, a}] : [];
              });
            if (!visibles.length) return null;
            return (
              <div key={i} className="flex flex-col gap-1">
                {seccion.titulo &&
                  (contraido ? (
                    <hr className="my-2 border-linea" />
                  ) : (
                    <div className="mt-3 px-3 text-[11px] font-semibold tracking-wide text-apagado uppercase">
                      {trx(seccion.titulo)}
                    </div>
                  ))}
                {visibles.map(e => enlace(e))}
              </div>
            );
          })}
          <div className="mt-auto pt-2">
            <AsistenteDeAyuda contraido={contraido} />
          </div>
        </nav>
        <div className={`border-t border-linea py-3 text-xs ${contraido ? 'px-2' : 'px-4'}`}>
          <NavLink
            to="/cuenta"
            className={`block rounded-md py-1 hover:bg-papel ${contraido ? 'text-center' : 'px-1'}`}
            title={contraido ? `${usuario.nombre} · ${tr('Mi cuenta', 'My account')}` : tr('Mi cuenta', 'My account')}
          >
            {contraido ? (
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-verde-oscuro font-semibold text-dorado">
                {iniciales(usuario.nombre)}
                <span className="sr-only">{tr('Mi cuenta', 'My account')}</span>
              </span>
            ) : (
              <>
                <div className="truncate font-medium text-tinta">{usuario.nombre}</div>
                <div className="text-apagado">{usuario.rolEtiqueta}</div>
              </>
            )}
          </NavLink>
          <div className={`mt-1 flex items-center gap-2 px-1 ${contraido ? 'flex-col' : 'justify-between'}`}>
            <button
              type="button"
              onClick={() => void cerrarSesion()}
              className="text-apagado hover:text-tinta"
              title={contraido ? tr('Salir', 'Sign out') : undefined}
            >
              {contraido ? (
                <>
                  <span aria-hidden>⎋</span>
                  <span className="sr-only">{tr('Salir', 'Sign out')}</span>
                </>
              ) : (
                tr('Salir', 'Sign out')
              )}
            </button>
            <button
              type="button"
              onClick={alternarTema}
              className="rounded-md px-1 text-apagado hover:bg-papel hover:text-tinta"
              aria-label={tr(`Tema: ${nombreDeTema(tema)}. Cambiar`, `Theme: ${nombreDeTema(tema)}. Change`)}
              title={tr('Cambiar entre claro y oscuro', 'Switch between light and dark')}
            >
              {iconoDeTema(tema)}
            </button>
            {!contraido && <SelectorDeIdioma guardarEnPerfil />}
          </div>
        </div>
        {!contraido && (
          <div className="border-t border-linea px-5 py-3 text-xs text-apagado">Sun Valley Investments</div>
        )}
      </aside>
      <main className="min-w-0 flex-1 overflow-auto px-4 py-4 md:px-8 md:py-6">
        {!esAncho && (
          <div className="-mx-4 -mt-4 mb-4 flex items-center gap-3 border-b border-linea bg-white px-4 py-2">
            <button
              type="button"
              onClick={() => setAbiertoEnCelular(true)}
              aria-expanded={abiertoEnCelular}
              className="rounded-md border border-linea px-2.5 py-1 text-sm"
            >
              ☰ {tr('Menú', 'Menu')}
            </button>
            <span className="marca font-semibold text-verde-oscuro">SUN VALLEY</span>
            {alertasNuevas > 0 && (
              <span className="ml-auto rounded-full bg-alerta px-1.5 text-[10px] leading-4 font-semibold text-white">
                {alertasNuevas}
              </span>
            )}
          </div>
        )}
        <Outlet />
      </main>
      {usuario.preferencias.avisosDeLogros !== false && <AvisoDeLogros />}
      <DescansaPantalla activo={usuario.preferencias.descansaPantalla !== false} />
    </div>
  );
}
